import { useEffect, useState } from "react";
import { Boxes, ShieldAlert, Save, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { invalidatePortalSettings } from "@/hooks/usePortalSettings";

/**
 * Admin: portal-level knobs.
 *  • Force 21+ age gate on the 3D Printing side (flip on when NSFW prints
 *    are listed, off when the catalog is clean-only).
 *  • View the current 3D Prints waitlist.
 */
export default function AdminPortal() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gateForced, setGateForced] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);

  useEffect(() => { void (async () => {
    try {
      const [s, w] = await Promise.all([
        api.get("/settings/portal"),
        api.get("/admin/prints/waitlist"),
      ]);
      setGateForced(!!s.data?.prints_gate_forced);
      setWaitlist(w.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load portal settings");
    } finally {
      setLoading(false);
      setWaitlistLoading(false);
    }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/portal", { prints_gate_forced: gateForced });
      invalidatePortalSettings();
      toast.success("Portal settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-portal">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Boxes className="h-4 w-4 text-fuchsia-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">Portal · 3D Printing</span>
        </div>
        <h2 className="font-display text-2xl tracking-[0.05em] text-white">Portal Settings</h2>
        <p className="text-white/55 text-sm mt-1">Controls the front-door chooser and the 3D Printing side of the studio.</p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-5">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-400/10 border border-amber-300/30 flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5 text-amber-300" />
            </div>
            <div>
              <div className="text-white font-medium">Force 21+ age gate on 3D Printing</div>
              <div className="text-sm text-white/55 mt-1 max-w-md">
                Turn this on if any listed print is NSFW/adult. Every visitor to <span className="font-mono text-white/80">/prints</span> will have to clear the 21+ gate before browsing.
              </div>
            </div>
          </div>
          <Switch
            data-testid="admin-portal-gate-toggle"
            checked={gateForced}
            onCheckedChange={setGateForced}
            disabled={loading}
          />
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            data-testid="admin-portal-save"
            onClick={save}
            disabled={loading || saving}
            className="bg-fuchsia-400 text-black hover:bg-fuchsia-300"
          >
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/80 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-fuchsia-300" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">Waitlist · {waitlist.length}</div>
        </div>
        {waitlistLoading ? (
          <div className="text-white/50 text-sm">Loading…</div>
        ) : waitlist.length === 0 ? (
          <div className="text-white/50 text-sm" data-testid="admin-portal-waitlist-empty">No signups yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {waitlist.map((row) => (
              <li key={row.email + row.created_at} className="py-2 flex items-center justify-between text-sm">
                <span className="text-white" data-testid="admin-portal-waitlist-email">{row.email}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">{new Date(row.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
